/* © CNES */
import { SelectButton, SelectButtonChangeEvent } from 'primereact/selectbutton';
import {
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  filterSections,
  Missions,
} from '../../../../../assets/config/filters/Referenciel';
import { FilterDataContext } from '../../../../../context/FilterDataContext';
import { SelectButtonItem } from '../../../../../models/SelectButtonItem';
import './FilterSelectButtons.scss';

/**
 * Interface for FilterSelectButtons props.
 */
export interface FilterSelectButtonsProps {
  name: any;
  options: SelectButtonItem[];
  handleChange: (e: any) => void;
  defaultAll?: boolean;
}

/**
 * FilterSelectButtons display a set of buttons to filter items.
 * @param name the name of the filter.
 * @param options all filter options to display as button.
 * @param handleChange how to handle button status.
 * @param defaultAll by default a filter has the All button selected.
 * @returns FilterSelectButtons Element.
 */
export const FilterSelectButtons = ({
  name,
  options,
  handleChange,
  defaultAll = true,
}: FilterSelectButtonsProps) => {
  const { filterData } = useContext(FilterDataContext);

  const defaultSelected: number[] | null = useMemo(() => {
    if (filterData[name]) {
      return options
        .filter((option) => {
          // handle the case when it is a POSTEL or GEOV2 product
          if (
            Array.from(filterData[name]!)?.find((dataType) =>
              dataType.includes('POSTEL')
            )
          ) {
            return (
              JSON.stringify(option.key) === JSON.stringify(filterData[name])
            );

            // handle the case of other collection
          } else if (Array.isArray(option.key)) {
            return Array.from(filterData[name]!)?.some((value) =>
              option.key.includes(value)
            );
          } else {
            return filterData[name]?.includes(option.key);
          }
        })
        .map((el) => el.value);
    }
    return null;
  }, [filterData, options, name]);
  const [selectAll, setSelectAll] = useState<string | null>(null);

  useEffect(() => {
    setSelectAll(defaultAll && !defaultSelected?.length ? 'All' : null);
  }, [defaultAll, defaultSelected]);

  const [selectValues, setSelectValues] = useState<number[] | string | null>(
    defaultSelected
  );
  const [optionsBlue, setOptionsBlue] = useState<SelectButtonItem[]>([]);
  const [optionsGreen, setOptionsGreen] = useState<SelectButtonItem[]>([]);
  const allOptions: string[] = useMemo(() => ['All'], []);

  useEffect(() => {
    setSelectValues(defaultSelected);
  }, [defaultSelected]);

  useEffect(() => {
    if (
      name === filterSections.dataProduct.param ||
      name === filterSections.productType.param ||
      name === filterSections.variable.param
    ) {
      setOptionsBlue(options.filter((option) => option.color === 'blue'));
      setOptionsGreen(options.filter((option) => option.color === 'green'));
    }
  }, [name, options]);

  /**
   * Handle when a button is clicked.
   * @param value the button status resulting from click.
   */
  const handleOptionChange = (
    value: string | number[],
    originalEvent?: SyntheticEvent<Element, Event>
  ) => {
    const input = originalEvent?.target as HTMLElement;
    let filterValue: string | number[] = [];

    if (
      (value === 'All' || value === null) &&
      (selectValues === null || selectValues.length === 0)
    ) {
      filterValue = options.map((el) => el.value);
      setSelectValues(null);
      handleChange(filterValue);
    } else if (value === 'All' && selectValues !== null) {
      setSelectValues(null);
      handleChange([]);
    } else {
      if (
        input?.innerText === 'Postel' &&
        typeof value === 'object' &&
        value.includes(Missions.postel)
      ) {
        filterValue = [Missions.postel];
      } else if (
        input?.innerText === 'GEOV2 AVHRR' &&
        typeof value === 'object' &&
        value.includes(Missions.GEOV2_AVHRR)
      ) {
        filterValue = [Missions.GEOV2_AVHRR];
      } else if (
        input?.innerText !== 'Postel' &&
        input?.innerText !== 'GEOV2 AVHHR' &&
        !isNotMissionSection &&
        typeof value === 'object' &&
        (value.includes(Missions.postel) ||
          value.includes(Missions.GEOV2_AVHRR))
      ) {
        filterValue = value.filter(
          (el) => el !== Missions.postel && el !== Missions.GEOV2_AVHRR
        );
      } else {
        filterValue = value;
      }

      setSelectAll(null);
      setSelectValues(value);
      handleChange(filterValue);
    }
  };

  const isNotMissionSection = useMemo(() => {
    return filterSections.mission.param !== name;
  }, [name]);

  return (
    <div className="filter-buttons">
      <div className="all-button">
        {isNotMissionSection && (
          <SelectButton
            value={selectAll}
            onChange={(e: SelectButtonChangeEvent) =>
              handleOptionChange(e.value)
            }
            options={allOptions}
          />
        )}
      </div>
      <div
        className={`filters ${isNotMissionSection && 'not-mission-section'}`}
      >
        {!optionsBlue.length && !optionsGreen.length && (
          <SelectButton
            value={selectValues}
            onChange={(e: SelectButtonChangeEvent) =>
              handleOptionChange(e.value, e.originalEvent)
            }
            optionLabel="name"
            options={options}
            multiple
          />
        )}
        {!!optionsBlue.length && (
          <SelectButton
            className="blueOption"
            value={selectValues}
            onChange={(e: SelectButtonChangeEvent) =>
              handleOptionChange(e.value)
            }
            optionLabel="name"
            options={optionsBlue}
            multiple
          />
        )}
        {!!optionsGreen.length && (
          <SelectButton
            className={
              optionsBlue.length ? 'greenOption marginOption' : 'greenOption'
            }
            value={selectValues}
            onChange={(e: SelectButtonChangeEvent) =>
              handleOptionChange(e.value)
            }
            optionLabel="name"
            options={optionsGreen}
            multiple
          />
        )}
      </div>
    </div>
  );
};
